<template>
  <ScrollBar class="editor-property-bar" :item-gap="0" :scrollbar-offset="0">
    <BackgroundMusic
      ref="backgroundMusicRef"
      :music-name="bgmDisplayName"
      :initial-volume="bgmInitialVolume"
      :initial-content-url="bgmInitialContentUrl"
      @confirm="handleMusicConfirm"
    />

    <ImageContent
      v-show="!isCurrentPageBackCover"
      :image-url="imageUrl"
      :crop="imageCrop"
      @ai-generate="handleAiGenerate"
      @local-upload="handleLocalUpload"
    />

    <AiGenerateDialog
      v-model="showAiGenerateDialog"
      :initial-prompt="aiInitialPrompt"
      :story-id="Number(store.id)"
      :page="store.currentPage?.originIndex ?? 0"
      @generated="handleAiGenerated"
    />

    <PageVoiceover
      v-show="hasTextElement && !isCurrentPageBackCover"
      :audio-url="ttsAudioUrl"
      :voice-name="ttsVoiceName"
      :has-tts="hasPageTts"
      :is-playing="isTtsPlaying"
      :portrait="ttsPortrait"
      :description="ttsDescription"
      @confirm="handleVoiceConfirm"
      @play-audio="handlePlayPageAudio"
    />

    <TextProperties
      v-show="hasTextElement && !isCurrentPageBackCover"
      v-model:text-style="textStyle"
      v-model:text="textContent"
      :hide-apply-all="isCurrentPageCover || isCurrentPageBackCover"
      @toggle-style="handleToggleStyle"
      @select-fill-color="handleSelectFillColor"
      @select-stroke-color="handleSelectStrokeColor"
      @apply-to-all="handleApplyToAll"
    />
  </ScrollBar>
</template>

<script setup lang="ts">
  import { computed, ref, toRaw } from 'vue';
  import type { TextStyleData } from '@/types/pixiText-type';
  import type { ElementData, TextContent } from '@/types';
  import { ELEMENT_TYPE, EVENT_NAMES, ACTION_TYPE } from '@/constants';
  import { useProjectStore } from '@/store/project';
  import { emitter, getAbsolutePath, hideLoading, showLoading } from '@/utils';
  import AudioPlayer from '@/utils/AudioPlayer';
  import type { MusicSelectResult } from './tool/MusicSelectDialog.vue';
  import type { VoiceSelectResult } from '@/views/manage/create/VoiceSelectDialog.vue';
  import { uploadImageFile } from '@/api/upload';
  import { createTts, type CreateTtsPageInput } from '@/api/tts';
  import {
    createImageActionData,
    createSoundActionData
  } from '@/modules/editor/canvas/models/template/dataTemplate';
  import ScrollBar from '@/components/common/list/ScrollBar.vue';
  import BackgroundMusic from './property/BackgroundMusic.vue';
  import ImageContent from './property/ImageContent.vue';
  import PageVoiceover from './property/PageVoiceover.vue';
  import TextProperties from './property/TextProperties.vue';

  import AiGenerateDialog from './tool/AiGenerateDialog.vue';
  import { ElMessage } from 'element-plus';

  const store = useProjectStore();

  // ============================================================
  // AI 生成弹窗状态
  // ============================================================

  const showAiGenerateDialog = ref(false);

  const aiInitialPrompt = computed(() => {
    return store.currentPage?.aiMeta?.scene_description ?? '';
  });

  // ============================================================
  // 当前页首个元素查找
  // ============================================================

  /** 当前页第一个图片/GIF action */
  const firstImageAction = computed(
    () =>
      store.currentPage?.actions?.find(
        (a) => a.element?.type === ELEMENT_TYPE.IMAGE || a.element?.type === ELEMENT_TYPE.GIF
      ) ?? null
  );

  /** 当前页第一个文本 action（按 action.type 查找） */
  const firstTextAction = computed(
    () => store.currentPage?.actions?.find((a) => a.type === ACTION_TYPE.TEXT) ?? null
  );

  const hasTextElement = computed(() => !!firstTextAction.value);

  /** 当前页是否为封面页（封面页隐藏"应用到所有"按钮） */
  const isCurrentPageCover = computed(() => store.currentPage?.type === 'cover');

  /** 当前页是否为封底页（封底页仅显示背景音乐） */
  const isCurrentPageBackCover = computed(() => store.currentPage?.type === 'backCover');

  // ============================================================
  // 当前页配音 —— 从 currentPage.actions 中查找 SOUND 类型
  // ============================================================

  /** 当前页第一个音频 action（按 action.type === SOUND 查找） */
  const firstSoundAction = computed(
    () => store.currentPage?.actions?.find((a) => a.type === ACTION_TYPE.SOUND) ?? null
  );

  const ttsAudioUrl = computed<string | null>(() => {
    // 优先从 SOUND action 的 element.content 获取
    const content = firstSoundAction.value?.element?.content;
    if (typeof content === 'string') return content;
    // 回退到 page.tts.url
    return store.currentPage?.tts?.url ?? null;
  });

  const ttsVoiceName = computed<string | null>(() => {
    // 优先从 SOUND action 的 element.name 获取
    const name = firstSoundAction.value?.element?.meta?.name;
    if (name) return name;
    // 回退到 page.tts.text（TTS 语音文本用于展示）
    return store.currentPage?.tts?.text || null;
  });

  const ttsPortrait = computed<string | null>(() => {
    const portrait = firstSoundAction.value?.element?.meta?.portrait;
    if (portrait) return portrait;
    return store.currentPage?.tts?.portrait ?? null;
  });

  const ttsDescription = computed<string | null>(() => {
    const desc = firstSoundAction.value?.element?.meta?.description;
    if (desc) return desc;
    return store.currentPage?.tts?.description ?? null;
  });

  const hasPageTts = computed(() => !!firstSoundAction.value || !!store.currentPage?.tts);

  /** TTS 播放状态 */
  const isTtsPlaying = ref(false);

  // ============================================================
  // BGM —— 从 project store 的 bgm 字段读写（项目级，非页面级）
  // ============================================================

  const bgmDisplayName = computed(() => {
    if (!store.bgm) return '无';
    return store.bgm.element?.name || '背景音乐';
  });

  const bgmInitialVolume = computed(() => {
    const vol = store.bgm?.element?.config?.volume;
    return vol ?? 0.3;
  });

  const bgmInitialContentUrl = computed<string | null>(() => {
    const content = store.bgm?.element?.content;
    return typeof content === 'string' ? content : null;
  });

  function handleMusicConfirm(result: MusicSelectResult) {
    // 清除背景音乐
    if (result.id === null) {
      emitter.emit(EVENT_NAMES.BGM_ADD, null);
      return;
    }

    // 区分上传 / 曲库选中，提取 url 和 name
    const isUpload = result.id === '__upload__';
    const url = isUpload ? result.contentUrl : (result.track?.url ?? result.track?.name);
    const name = isUpload ? (result.trackName ?? '自定义上传') : result.track?.name;

    if (url && name) {
      emitter.emit(EVENT_NAMES.BGM_ADD, { url, name, volume: result.volume });
    }
  }

  // ============================================================
  // 只读 computed —— 子组件只展示，不写回
  // ============================================================

  /** 图片 URL —— ImageContent 只读预览，通过 @local-upload 替换 */
  const imageUrl = computed(() => {
    const content = firstImageAction.value?.element?.content;
    return typeof content === 'string' ? content : '';
  });

  /** 图片裁剪状态 —— 传给 ImageContent 显示裁剪指示器 */
  const imageCrop = computed(() => {
    return firstImageAction.value?.element?.config?.crop;
  });

  // ============================================================
  // textStyle v-model —— 双向绑定，子组件通过 emit('update:textStyle', partial) 写回
  // ============================================================

  /** 文本样式 —— 读写当前页第一个文本元素的 style */
  const textStyle = computed<TextStyleData | undefined>({
    get() {
      const content = firstTextAction.value?.element?.content;
      if (content && typeof content !== 'string' && 'style' in content) {
        return (content as TextContent).style;
      }
      return undefined;
    },
    set(val) {
      if (!val) return;
      const element = firstTextAction.value?.element;
      const content = element?.content;
      if (!element || !content || typeof content === 'string' || !('style' in content)) return;

      const textContent = content as TextContent;
      const merged = mergeTextStyle(textContent.style ?? {}, val);

      // 构造更新后的 element（浅拷贝即可，仅 style 字段变化）
      const updated: ElementData = {
        ...toRaw(element),
        content: { ...textContent, style: merged }
      };

      emitter.emit(EVENT_NAMES.ELEMENT_UPDATE, updated);
    }
  });

  /** 文本内容 —— 读写当前页第一个文本元素的 text 字段 */
  const textContent = computed<string>({
    get() {
      const content = firstTextAction.value?.element?.content;
      if (content && typeof content !== 'string' && 'text' in content) {
        return (content as TextContent).text ?? '';
      }
      return '';
    },
    set(val) {
      const element = firstTextAction.value?.element;
      const content = element?.content;
      if (!element || !content || typeof content === 'string' || !('text' in content)) return;

      const textContent = content as TextContent;
      const updated: ElementData = {
        ...toRaw(element),
        content: { ...textContent, text: val }
      };

      emitter.emit(EVENT_NAMES.ELEMENT_UPDATE, updated);
    }
  });

  // ============================================================
  // 样式合并工具
  // ============================================================

  /** 这些 key 整体替换不做深度合并；传 null 表示删除 */
  const REPLACE_KEYS = new Set<keyof TextStyleData>(['fill', 'stroke', 'shadow']);

  function mergeTextStyle(target: TextStyleData, source: TextStyleData): TextStyleData {
    const result = { ...target };
    for (const key of Object.keys(source) as (keyof TextStyleData)[]) {
      const src = source[key];
      if (REPLACE_KEYS.has(key)) {
        if (src === null) {
          delete result[key];
        } else {
          (result as Record<string, unknown>)[key] = src;
        }
      } else if (src !== null && typeof src === 'object' && !Array.isArray(src)) {
        (result as Record<string, unknown>)[key] = {
          ...(result[key] as Record<string, unknown>),
          ...src
        };
      } else {
        (result as Record<string, unknown>)[key] = src;
      }
    }
    return result;
  }

  // ============================================================
  // 事件处理
  // ============================================================

  function handleAiGenerate() {
    showAiGenerateDialog.value = true;
  }

  function handleAiGenerated(imageUrl: string, prompt: string) {
    // 将生成时使用的 prompt 更新回 store
    if (store.currentPage?.aiMeta) {
      store.currentPage.aiMeta.scene_description = prompt;
    }

    if (!imageUrl) {
      console.warn('[EditorPropertyBar] 生成结果为空');
      return;
    }

    const actionId = firstImageAction.value?.id;
    if (actionId) {
      // 已有图片元素，替换内容
      emitter.emit(EVENT_NAMES.ACTION_RELOAD, {
        actionId,
        content: imageUrl
      });
    } else {
      // 没有图片元素，创建新的图片元素
      const actionData = createImageActionData(imageUrl);
      emitter.emit(EVENT_NAMES.ACTION_ADD, actionData);
    }
  }

  async function handleLocalUpload(file: File) {
    try {
      showLoading({ text: '正在上传图片...' });
      const imageUrl = await uploadImageFile(file);

      const actionId = firstImageAction.value?.id;
      if (actionId) {
        // 已有图片元素，替换内容
        emitter.emit(EVENT_NAMES.ACTION_RELOAD, { actionId, content: imageUrl });
      } else {
        // 没有图片元素，创建新的图片元素
        emitter.emit(EVENT_NAMES.ACTION_ADD, createImageActionData(imageUrl));
      }
    } catch (err: any) {
      ElMessage.error(`${err.msg ?? err}`);
    } finally {
      hideLoading();
    }
  }

  function handleToggleStyle() {
    // TODO: 打开文本样式选择面板
  }

  function handleSelectFillColor() {
    // TODO: 打开填充颜色选择器
  }

  function handleSelectStrokeColor() {
    // TODO: 打开描边颜色选择器
  }

  /** TTS 生成中状态 */
  const isTtsGenerating = ref(false);

  /** 配音角色确认 —— 为当前页生成 TTS 配音 */
  async function handleVoiceConfirm(result: VoiceSelectResult) {
    const { voice, volume, speed } = result;
    const page = store.currentPage;
    if (!page) return;

    // 从当前页提取文本
    const textActions = page.actions?.filter((a) => a.type === ACTION_TYPE.TEXT) ?? [];
    const text = textActions
      .map((a) => {
        const content = a.element?.content;
        if (content && typeof content !== 'string' && 'text' in content) {
          return (content as TextContent).text;
        }
        return '';
      })
      .filter(Boolean)
      .join('');

    if (!text) {
      ElMessage.error('当前页没有可配音的文本内容');
      return;
    }

    const pageIndex = page.originIndex ?? 0;
    const pages: CreateTtsPageInput[] = [{ page: pageIndex, text }];

    isTtsGenerating.value = true;
    try {
      showLoading({ text: '正在生成配音...' });
      const ttsResult = await createTts({
        id: String(store.id),
        voice: {
          roleId: voice.id,
          source: voice.provider,
          pitchRate: 0,
          speed,
          volume,
          name: voice.name,
          description: voice.description,
          portrait: voice.portrait,
          category: voice.category
        },
        pages
      });

      const audio = ttsResult.audio.find((a) => a.page === pageIndex);

      if (!audio) {
        ElMessage.error('配音生成失败：未获取到音频');
        return;
      }

      // 1. 写回 page.tts
      const ttsMeta = {
        url: audio.url,
        text: audio.text,
        name: voice.name,
        description: voice.description,
        portrait: voice.portrait,
        category: voice.category,
        roleId: voice.id,
        source: voice.provider,
        volume,
        speed
      };
      page.tts = ttsMeta;

      // 2. 写回 SOUND action 的 element（content + meta）
      const soundAction = page.actions?.find((a) => a.type === ACTION_TYPE.SOUND) ?? null;

      if (soundAction?.element) {
        // 已有 SOUND action：更新 meta（服务器原地替换文件，content URL 不变）
        soundAction.element.meta = {
          ...(soundAction.element.meta ?? {}),
          name: voice.name,
          description: voice.description,
          portrait: voice.portrait,
          category: voice.category,
          roleId: voice.id,
          source: voice.provider,
          volume,
          speed
        };

        // 通知 canvas 重新加载音频元素，释放 AudioPool 中缓存的旧音频
        emitter.emit(EVENT_NAMES.ACTION_RELOAD, {
          actionId: soundAction.id,
          content: soundAction.element.content as string
        });
      } else {
        // 原先没有音频：创建新的 SOUND action 并触发 ADD 事件
        // audio.url 为相对路径（./ 开头），需拼接 baseUrl 得到完整 URL
        const absoluteUrl = getAbsolutePath(store.baseUrl, audio.url);
        const soundActionData = createSoundActionData(
          absoluteUrl,
          voice.name,
          { volume },
          {
            name: voice.name,
            description: voice.description,
            portrait: voice.portrait,
            category: voice.category,
            roleId: voice.id,
            source: voice.provider,
            volume,
            speed
          }
        );
        // 保留原始相对路径，与 resolveProjectPaths 保持一致
        if (soundActionData.element) {
          soundActionData.element.relativePath = audio.url;
        }
        emitter.emit(EVENT_NAMES.ACTION_ADD, soundActionData);
      }

      store.isModify = true;
      ElMessage.success('配音生成完成');
    } catch (err: any) {
      ElMessage.error(`配音生成失败:${err.message ?? err}`);
    } finally {
      hideLoading();
      isTtsGenerating.value = false;
    }
  }

  /** 播放 / 停止当前页配音音频 */
  function handlePlayPageAudio() {
    const url = ttsAudioUrl.value;
    if (!url) return;

    const player = AudioPlayer.instance;

    if (isTtsPlaying.value) {
      player.stop();
      isTtsPlaying.value = false;
    } else {
      const reset = () => {
        isTtsPlaying.value = false;
      };
      // 加时间戳参数避免浏览器缓存旧音频
      const cacheBustedUrl = `${url}${url.includes('?') ? '&' : '?'}_t=${Date.now()}`;
      player.play(cacheBustedUrl, reset, reset);
      isTtsPlaying.value = true;
    }
  }

  /** 将当前文本样式复制到所有页面的所有文本元素 */
  function handleApplyToAll() {
    if (!textStyle.value) return;
    emitter.emit(EVENT_NAMES.APPLY_ALL_TEXTSTYLE, textStyle.value);
  }
</script>

<style scoped>
  .editor-property-bar {
    width: 100%;
    height: 100%;
    background-color: #fff;
  }
</style>
